import {every, isEmpty, isEqual} from "lodash";
import {inject, injectable} from "inversify";
import type {ChangeEvent, SyntheticEvent} from "react";
import {action, computed, makeAutoObservable, observable} from "mobx";
import {AxiosError} from "axios";
import {RequestState} from "data/enums";
import {extractErrorMessage} from "data/utils";
import {Bindings} from "data/constants/bindings";
import type {IApiResponse} from "data/services/http";
import {ViewController} from "data/types/structure";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {IUpdateUserPayload} from "data/providers/api/user.api.provider";

export interface IUpdateAccountController extends ViewController {
	readonly i18n: ILocalizationStore;
	get isButtonDisabled(): boolean;
	get isValidForm(): boolean;
	get error(): Record<string, string>;
	get state(): Record<string, string>;

	handleFormSubmit: (event: SyntheticEvent<HTMLFormElement>) => void;
	handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class UpdateAccountController implements IUpdateAccountController {
	@observable private _requestState = RequestState.IDLE;
	@observable private _errorMsg: Record<string, string> = {};
	@observable private _formState = {
		email: "",
		displayName: "",
	};

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore
	) {
		makeAutoObservable(this);

		const user = this._userStore.user;

		if (user) {
			this._formState = {
				displayName: user.displayName,
				email: user.email,
			};
		}
	}

	get state() {
		return this._formState;
	}

	get isButtonDisabled() {
		return (
			isEqual(this._requestState, RequestState.PENDING) ||
			!this.isValidForm ||
			!this.formHasChanges
		);
	}

	get error() {
		return this._errorMsg;
	}

	@computed get isValidForm() {
		return every(this._formState, (value) => !isEmpty(value.trim()));
	}

	private get formHasChanges() {
		const user = this._userStore.user;

		const newData = {
			email: this._formState.email,
			displayName: this._formState.displayName,
		};

		const data = {
			email: user?.email,
			displayName: user?.displayName,
		};

		return !isEqual(data, newData);
	}

	private reportError(error: string, place: string = "") {
		this._errorMsg = {[place]: error};

		return true;
	}

	public handleFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
		e.preventDefault();

		const user = this._userStore.user;

		if (!user) {
			return;
		}

		const {displayName, email}: Record<string, HTMLInputElement> = e.currentTarget;

		const validateList = [
			{
				field: displayName,
				error: this.i18n.t("profile.form.invalid_display_name"),
				place: "displayName",
			},
			{
				field: email,
				error: this.i18n.t("profile.form.invalid_email"),
				place: "email",
			},
		];

		const hasError = validateList.some(({field, error, place}) =>
			field.checkValidity() ? false : this.reportError(error, place)
		);

		if (hasError) {
			return;
		}

		const payload: IUpdateUserPayload = {
			displayName: displayName.value.trim(),
			email: email.value.trim(),
			isNotificationsEnabled: user.isNotificationsEnabled,
			isOptIn: user.isOptIn,
		};

		this._requestState = RequestState.PENDING;

		this._userStore.update(payload).then(this.onSuccess).catch(this.onError);
	};

	@action private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	@action private onError = (error: AxiosError<IApiResponse<unknown>>) => {
		this._errorMsg = {common: extractErrorMessage(error)};
		this._requestState = RequestState.ERROR;
	};

	@action handleClearErrorOnChange = () => {
		this._errorMsg = {};
	};

	handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		this._formState = {
			...this._formState,
			[e.target.name]: e.target.value,
		};

		this.handleClearErrorOnChange();
	};
}
